import { createHash } from 'crypto';

import type {
  CoherentBandTotal,
  DealCommitmentV1,
  DealMeasurementBindingV1,
  MeasurementBindingStatus,
  PromiseBand,
  PromiseSetDraft,
  PromiseSetRevision,
  PromiseSetV1,
  ValueBasis,
} from './ma-deal-case';
import type { DealMeasurementBindingRef } from './ma-deal-refs';
import { maDealPromiseSetGovernanceVerdict } from './ma-deal-pack.validation';

export class PromiseSetImmutableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PromiseSetImmutableError';
  }
}

export class PromiseSetDigestMismatchError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PromiseSetDigestMismatchError';
  }
}

export class IncoherentPromiseBandError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'IncoherentPromiseBandError';
  }
}

export class MixedValueBasisError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MixedValueBasisError';
  }
}

export type MeasurementBindingValidationContext = Readonly<{
  measurementBindingRef: DealMeasurementBindingRef;
  validated: boolean;
  catalogAdmitted: boolean;
}>;

export function computeContentDigest(promiseSet: PromiseSetV1 | PromiseSetDraft): string {
  const canonical = canonicalJson(promiseSet);
  return `sha256:${createHash('sha256').update(canonical).digest('hex')}`;
}

export function approvePromiseSet(draft: PromiseSetDraft): PromiseSetV1 {
  if (draft.governanceState !== 'draft') {
    throw new PromiseSetImmutableError('Only a draft promise set can be approved.');
  }

  const candidate: PromiseSetV1 = {
    ...draft,
    contentDigest: '',
    governanceState: 'approved',
  };
  assertApprovedPromiseSet(candidate);
  const contentDigest = computeContentDigest(candidate);
  assertDigestMatchesDraft(draft.contentDigest, contentDigest);
  return deepFreeze(copyPromiseSet({ ...candidate, contentDigest }));
}

export function revisePromiseSet(
  approved: PromiseSetV1,
  revision: PromiseSetRevision
): PromiseSetV1 {
  assertApprovedForRevision(approved);
  assertFreshPromiseSetIdentity(approved, revision);
  const draft = buildRevisionDraft(approved, revision);
  return approvePromiseSet(draft);
}

export function supersedePromiseSet(
  approved: PromiseSetV1,
  revision: PromiseSetRevision
): PromiseSetV1 {
  return revisePromiseSet(approved, revision);
}

export function measurementBindingStatus(
  commitment: DealCommitmentV1,
  binding: DealMeasurementBindingV1 | null | undefined,
  validation: MeasurementBindingValidationContext | null | undefined
): MeasurementBindingStatus {
  if (!binding || !commitment.measurementBindingRef) return 'not_measurable';
  if (binding.measurementBindingRef !== commitment.measurementBindingRef) return 'not_measurable';
  if (binding.commitmentRef !== commitment.commitmentRef) return 'not_measurable';
  if (binding.requiredEvidenceKeys.length === 0) return 'not_measurable';
  if (
    !validation ||
    validation.measurementBindingRef !== binding.measurementBindingRef ||
    !validation.validated ||
    !validation.catalogAdmitted
  ) {
    return 'not_measurable';
  }
  return 'bound';
}

export function coherentBandTotal(
  promiseSet: PromiseSetV1,
  band: PromiseBand
): CoherentBandTotal {
  if (!promiseSet.bandSemantics.coherentPortfolio) {
    throw new IncoherentPromiseBandError(
      'Band totals require coherent promise-set band semantics.'
    );
  }
  if (!promiseSet.bandSemantics.bands.includes(band)) {
    throw new IncoherentPromiseBandError(
      'Band totals require the requested band to be declared by the promise set.'
    );
  }
  const { commitments } = promiseSet;
  const first = commitments[0];
  if (!first) throw new IncoherentPromiseBandError('A coherent portfolio requires commitments.');
  assertCoherentBand(first, band);
  const initial = first.targetByBand[band];
  const total = commitments.slice(1).reduce((value, commitment) => {
    assertCoherentBand(commitment, band);
    assertSameValueBasis(initial.valueBasis, commitment.targetByBand[band].valueBasis);
    return value + commitment.targetByBand[band].value;
  }, initial.value);
  return { band, value: total, valueBasis: initial.valueBasis };
}

function buildRevisionDraft(
  approved: PromiseSetV1,
  revision: PromiseSetRevision
): PromiseSetDraft {
  if (revision.commitments) {
    assertReplacementCommitmentsDoNotClaimLineage(revision.commitments);
  }
  const commitments =
    revision.commitments ??
    cloneSupersededCommitments(approved, revision.promiseSetId);
  return {
    ...approved,
    ...revision,
    contentDigest: revision.contentDigest,
    governanceState: 'draft',
    version: approved.version + 1,
    supersedesPromiseSetRef: approved.promiseSetId,
    commitmentRefs:
      revision.commitmentRefs ?? commitments.map((commitment) => commitment.commitmentRef),
    commitments,
  };
}

function cloneSupersededCommitments(
  approved: PromiseSetV1,
  promiseSetRef: PromiseSetV1['promiseSetId']
): readonly DealCommitmentV1[] {
  return approved.commitments.map((commitment) => ({
    ...copyCommitment(commitment),
    promiseSetRef,
    supersedesCommitmentRef: commitment.commitmentRef,
  }));
}

function assertReplacementCommitmentsDoNotClaimLineage(
  commitments: readonly DealCommitmentV1[]
): void {
  for (const commitment of commitments) {
    if (commitment.supersedesCommitmentRef) {
      throw new PromiseSetImmutableError(
        'Replacement commitments cannot declare predecessor lineage.'
      );
    }
  }
}

function assertApprovedPromiseSet(promiseSet: PromiseSetV1): void {
  const verdict = maDealPromiseSetGovernanceVerdict(promiseSet);
  if (verdict.ok) return;
  if (verdict.code === 'commitment_economics') {
    throw new MixedValueBasisError(verdict.message);
  }
  throw new PromiseSetImmutableError(verdict.message);
}

function assertDigestMatchesDraft(provided: string | undefined, computed: string): void {
  if (provided !== undefined && provided !== computed) {
    throw new PromiseSetDigestMismatchError(
      'The supplied content digest does not match the promise set.'
    );
  }
}

function assertApprovedForRevision(approved: PromiseSetV1): void {
  if (approved.governanceState !== 'approved') {
    throw new PromiseSetImmutableError('Only an approved promise set can be superseded.');
  }
  assertApprovedPromiseSet(approved);
  const computedDigest = computeContentDigest(approved);
  if (computedDigest !== approved.contentDigest) {
    throw new PromiseSetDigestMismatchError(
      'The approved promise-set content digest does not match its content.'
    );
  }
}

function assertFreshPromiseSetIdentity(approved: PromiseSetV1, revision: PromiseSetRevision): void {
  if (revision.promiseSetId === approved.promiseSetId) {
    throw new PromiseSetImmutableError('A revision must use a new promise-set identity.');
  }
}

function assertCoherentBand(commitment: DealCommitmentV1, band: PromiseBand): void {
  if (!commitment.bandSemantics.coherentPortfolio) {
    throw new IncoherentPromiseBandError('Band totals require coherent portfolio semantics.');
  }
  if (!commitment.bandSemantics.bands.includes(band)) {
    throw new IncoherentPromiseBandError('Band totals require the requested band to be declared.');
  }
}

function assertSameValueBasis(expected: ValueBasis, actual: ValueBasis): void {
  if (expected !== actual) {
    throw new MixedValueBasisError('Band totals require the same value basis.');
  }
}

function canonicalJson(value: unknown): string {
  if (value === null || typeof value === 'boolean' || typeof value === 'number') {
    return stringifyPrimitive(value);
  }
  if (typeof value === 'string') return stringifyPrimitive(value);
  if (Array.isArray(value)) return `[${value.map(canonicalJson).sort().join(',')}]`;
  if (isPlainRecord(value)) return canonicalRecord(value);
  throw new TypeError('Promise-set content must contain JSON-compatible values.');
}

function canonicalRecord(value: Readonly<Record<string, unknown>>): string {
  return `{${Object.entries(value)
    // Promise identity is the approved economic case. Reader labels, standing measurement
    // bindings and later realization certainty are governed metadata, not promise revisions.
    .filter(
      ([key]) =>
        key !== 'contentDigest' &&
        key !== 'displayLabel' &&
        key !== 'certaintyClass',
    )
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, entry]) =>
      `${stringifyPrimitive(key)}:${canonicalJson(
        key === 'measurementBindingRef' ? null : entry,
      )}`,
    )
    .join(',')}}`;
}

function isPlainRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stringifyPrimitive(value: string | number | boolean | null): string {
  const serialized = JSON.stringify(value);
  if (serialized === undefined) {
    throw new TypeError('Promise-set content must contain JSON-compatible values.');
  }
  return serialized;
}

function deepFreeze<Value>(value: Value): Value {
  if (typeof value === 'object' && value !== null && !Object.isFrozen(value)) {
    for (const child of Object.values(value)) deepFreeze(child);
    Object.freeze(value);
  }
  return value;
}

function copyPromiseSet(promiseSet: PromiseSetV1): PromiseSetV1 {
  return {
    ...promiseSet,
    sourceDocumentRefs: [...promiseSet.sourceDocumentRefs],
    bandSemantics: {
      ...promiseSet.bandSemantics,
      bands: [...promiseSet.bandSemantics.bands],
    },
    commitmentRefs: [...promiseSet.commitmentRefs],
    commitments: promiseSet.commitments.map(copyCommitment),
  };
}

function copyCommitment(commitment: DealCommitmentV1): DealCommitmentV1 {
  return {
    ...commitment,
    targetByBand: {
      conservative: { ...commitment.targetByBand.conservative },
      base: { ...commitment.targetByBand.base },
      stretch: { ...commitment.targetByBand.stretch },
    },
    costToAchieve: { ...commitment.costToAchieve },
    thesisOutcomeRefs: [...commitment.thesisOutcomeRefs],
    bandSemantics: {
      ...commitment.bandSemantics,
      bands: [...commitment.bandSemantics.bands],
    },
  };
}
